'use client';
import React, { useRef, useState, useMemo, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import mapboxgl from 'mapbox-gl'
import { Avatar } from "@heroui/react"
import { point, along, length, lineString, greatCircle, bbox, bearing } from '@turf/turf'
import { FaCircle, FaFlagCheckered, FaMapMarker } from 'react-icons/fa'
import 'mapbox-gl/dist/mapbox-gl.css';

import ErrorBoundary from './error-boundary'
import { formatMetric, displayMeasurement, formatNumber, formatDuration } from '../src/formatter'

mapboxgl.accessToken = process.env.MAPBOX

const colorArr = [
  'bg-primary-100',
  'bg-primary-200',
  'bg-primary-300', 
  'bg-primary-400',
  'bg-primary-500',
  'bg-primary-600', 
  'bg-primary-700', 
  'bg-primary-800',
  'bg-primary-900'
]

const MapContainer = ({ map, challengeMeasure, challengeGoal, progressPoints, user, points, distanceCallback }) => {
  const [hasWebgl, setHasWebgl] = useState('unchecked')

  useEffect(() => {
    const wgl = detectWebGLContext()
    setHasWebgl(wgl ? 'active' : 'inactive')
    if (!wgl) return
  }, [])

  function detectWebGLContext () {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl")
    return gl && gl instanceof WebGLRenderingContext
  }

  if (!map || hasWebgl === 'unchecked') return <></>
  if (hasWebgl === 'inactive') {
    return <div className='text-center pt-12'>
      The map cannot be rendered.<br/>WebGl is disabled in your browser
    </div>
  }
  
  return <ErrorBoundary tryAgain='Try Loading Map Again' errorMsg = 'There was an error rendering the map.'>
    <Map
      map={map}
      challengeMeasure={challengeMeasure}
      challengeGoal={challengeGoal}
      progressPoints={progressPoints}
      user={user}
      points={points}
      distanceCallback={distanceCallback} />
  </ErrorBoundary>
}

const WaypointMarkers = ({ mapRef, waypoints, defaultIconColor, showStreetView  }) => {
  const [markers, setMarkers] = useState([])
  useEffect(() => {
    markers.forEach(marker => marker.remove()) // reset
    setMarkers(waypoints
      .filter(item => {
        if (item.coordinates && Array.isArray(item.coordinates) && item.coordinates.length >= 2) return true
        return false
      })
      .map((item, index) => {
        let popupContent = ''
        const markerNode = document.createElement('div')
        const root = createRoot(markerNode)
        if (item.place) {
          root.render(<div style={{ position: 'absolute', padding: '0px 3px', backgroundColor: '#fafafa', borderRadius: '4px', top: '-20px', left: '15px', border: '1px solid #ccc', whiteSpace: 'nowrap' }}>{item.place}</div>)
          popupContent = item.place
        } else if (!item.icon && index === 0) {
          if (item.coordinates && item.coordinates.length > 1 && waypoints.length > 1 && item.coordinates.join(',') === waypoints[waypoints.length - 1]?.coordinates?.join(',')) {
            // start and end are the same, so make start bigger to compose finish icon
            root.render(<FaCircle style={{ fontSize: '30px', color: '#E3650B' }} />)
          } else {  
            root.render(<FaCircle style={{ fontSize: '18px', color: '#E3650B' }} />)
            popupContent = `Start<br/><strong>${item.label || ''}</strong>`
          }
        } else if (!item.icon && index === waypoints.length - 1 && index > 0) {
          root.render(<FaFlagCheckered style={{ fontSize: '20px' }} />)
          if (item.coordinates.join(',') === waypoints[0].coordinates.join(',')) {
            popupContent = `Start and End<br/><strong>${item.label || ''}</strong>`
          } else {
            popupContent = `End<br/><strong>${item.label || ''}</strong>`
          }
        } else if (index > 0 && index !== waypoints.length - 1 && (item.label || item.place) && !item.hideDefaultIcon) {
          root.render(<FaMapMarker style={{ color: item.iconColor || (defaultIconColor || '#333'), fontSize: '18px' }} />)
          popupContent = item.label || item.place
        } else if (item.icon) {
          root.render(<img alt={item.label} className='mt-2 rounded-md' style={item.iconStyle || { width: '40px' }} src={item.icon} />) 
          popupContent = item.label
        }
        if (showStreetView) {
          popupContent += `<div className='center'><a style="outline: none; box-shadow: none;" href='http://maps.google.com/maps?layer=c&q=&cbll=${item.coordinates[1]},${item.coordinates[0]}' target='streetview'>Street View</a></div>`
        }  
        const popup = new mapboxgl.Popup({ offset: 25, closeButton: false })
          .setHTML(`<div style='text-align: center;'>${popupContent}</div>`)
        
        return new mapboxgl.Marker(markerNode)
          .setLngLat(item.coordinates)
          .setPopup(popup)
          .addTo(mapRef)
      })
    )
    return () => {
      markers.forEach(marker => marker.remove())
    }
  }, [mapRef, waypoints])
  return null // do not render anything  
}

const ParticipantMarkers = ({ mapRef, map, user, challengeMeasure, challengeGoal, progressPoints, points, currentLine }) => {
  const [markers, setMarkers] = useState([])
  const { distance, lineString, lineStrings } = currentLine
  
  let useChallengeGoal = map.useChallengeGoal || map.useChallengeDistance // useChallengeDistance is deprecated fields
  // edge case allow override of goal and measure. This can happen if challenge is activity
  // and customer wants map based on duration, for example. 
  if (!challengeGoal && map.challengeGoal && map.challengeMeasure) {
    challengeGoal = map.challengeGoal
    challengeMeasure = map.challengeMeasure
    useChallengeGoal = true
  }

  if (!challengeGoal) useChallengeGoal = false // cannot do calc if missing challengeGoal value
  if (challengeMeasure === 'pointGoal') challengeMeasure = 'points'
  if (challengeMeasure === 'durationGoal') challengeMeasure = 'duration'
  if (challengeMeasure === 'activityGoal') challengeMeasure = map.challengeMeasure || 'distance'
  
  useEffect(() => {
    if (!progressPoints || !progressPoints.length) return
    
    markers.forEach(marker => marker.remove())
    
    let buildOnDistance = 0
    
    let self
    const mrks = progressPoints
      .sort((a, b) => {
        if (a[challengeMeasure] === b[challengeMeasure]) return 0
        if (a[challengeMeasure] > b[challengeMeasure]) return 1
        return -1
      })
      .filter(item => Boolean(item[challengeMeasure])) // do not show zero distance
      .filter(item => {
        if (map.loop) return true
        if (user && item.id === user.id) return true
        if (progressPoints.length === 1) return true

        // convert with duration multiplier
        let testD = item[challengeMeasure]
        if (challengeMeasure === 'duration' && !useChallengeGoal) {
          testD = (((parseFloat(testD) / 60) * (map.durationMultiplier || 1)) / (user.imperial ? 0.0006213711922 : 1))
        }
        
        if (useChallengeGoal && 
            challengeGoal && 
            parseFloat(testD) < parseFloat(challengeGoal)) return true
        
        if (!useChallengeGoal && testD < distance) return true 
        return false
      })
      .map((item, index) => {
        let participantDistance = item[challengeMeasure]
        if (challengeMeasure === 'duration' && !item.challengeGoal) {
          participantDistance = (((parseFloat(participantDistance) / 60) * (map.durationMultiplier || 1)) / (user.imperial ? 0.0006213711922 : 1))
        }
        if (challengeMeasure === 'points' && points) {
          participantDistance = parseFloat(participantDistance * 1000) / (points.imperial ? 0.6213711922 : 1)
        }
        let actualParticipantDistance = participantDistance

        if (map.scaledProgress) {
          const sampleDist = participantDistance
          participantDistance += buildOnDistance
          buildOnDistance += sampleDist;
        }
        
        let cg = challengeGoal
        if (useChallengeGoal) {
          // from current distance, get pct of actual route done
          if (useChallengeGoal && challengeMeasure === 'duration') {
            cg = challengeGoal / 60
          }
          if (challengeMeasure === 'points' && points && points.imperial) {
            cg = cg / 0.0006213711922 // convert to metric (meters)
          }
          participantDistance = distance * (participantDistance / cg) 
        }

        // participantDistance can be mutated if using challengeGoal,
        // so use actualParticipantDistance for percentage calculation
        const percentComplete = actualParticipantDistance / (useChallengeGoal ? cg : distance)
        
        let laps
        let p, rotation
        
        if (map.loop) {
          laps = Math.ceil(participantDistance / distance)
          participantDistance = participantDistance % distance
        } else if (participantDistance > distance) {
          participantDistance = distance
          p = point(map.waypoints[map.waypoints.length - 1].coordinates)
        }
        
        if (!p) {
          try {
            p = along(lineString, participantDistance, { units: 'meters' })
            const p1 = along(lineString, participantDistance + 100, { units: 'meters' })
            rotation = bearing(p, p1)
          } catch (e) {
            if (!lineStrings) return
            let runningTotal = 0
            for (let i = 0; i < lineStrings.length; i++) {
              const currentLength = length(lineStrings[i], { units: 'meters' })
              runningTotal += currentLength
              if (participantDistance <= runningTotal) {
                const segLength = participantDistance - (runningTotal - currentLength)
                p = along(lineStrings[i], segLength, { units: 'meters' })
                const p1 = along(lineStrings[i], segLength + 100, { units: 'meters' })
                rotation = bearing(p, p1)
                break
              }
            }
          }
        }

        const randomColor = Math.floor(Math.random() * colorArr.length)
        const base = `${colorArr[randomColor]} ${randomColor > 3 ? 'text-white' : 'text-black'}`

        const initials = item.fullname
          .split(' ')
          .map(word => word.charAt(0).toUpperCase())
          .join('')

        if (item.icon) item.photo = item.icon
        if (item.photo?.match(/runner-/)) { // only for team challenges
          item.photo = rotation < 0 ? `/runner-orange-flipped.png` : `/runner-orange.png`
        }
        const markerNode = document.createElement('div')
        const root = createRoot(markerNode)
        root.render(<Avatar size='sm' key={index} src={item.photo} showFallback name={initials} classNames={{ base: base }} /> )
        
        const percentageHtml = !map.scaledProgress && challengeGoal ? `<div class='text-chSubtext'>${Math.round(percentComplete * 10000)/100}% complete</div>` : ""
        let stats = ''
        if (challengeMeasure === 'distance') {
          stats = `<div>${formatMetric(actualParticipantDistance, 2, 'lg', user.imperial)} ${displayMeasurement(user.imperial)}</div>`
        } else if (challengeMeasure === 'duration') {
          stats = `<div>${formatDuration(item[challengeMeasure])} minutes</div>`
        } else if (challengeMeasure === 'points') {
          stats = `<div>${formatNumber(item[challengeMeasure], 2)} points</div>`
        }
        let lapHtml = map.loop ? `<div>Lap ${laps}</div>` : ''

        let streetView = ''
        if (map.showStreetView) {
          streetView += `<div className='center'><a style="outline: none; box-shadow: none;" href='http://maps.google.com/maps?layer=c&q=&cbll=${p.geometry.coordinates[1]},${p.geometry.coordinates[0]}' target='streetview'>Street View</a></div>`
        }

        const popupName = ((user && user.id) || item.isTeamName) ? item.fullname : initials
        const popup = new mapboxgl.Popup({ className: 'mapPopup', offset: 25, closeButton: false })
          .setHTML(`<div style='text-align: center;'><strong>${popupName}</strong>${stats}${percentageHtml}${lapHtml}${streetView}</div>`)
        
        // Add the marker to the map
        const m = new mapboxgl.Marker(markerNode)
          .setLngLat(p.geometry.coordinates)
          .setPopup(popup)
          .addTo(mapRef)

        if ((user && item.id === user.id) || progressPoints.length === 1) {
          self = m
          const marker = m.getElement()
          marker.style.zIndex = '10' // bring self to front
          popup.addTo(mapRef)
        }

        return m
      })
    setMarkers(mrks)
    
    // if distance is less than 1/2 then onLoad fitToBounds will be used on route
    if (currentLine.distance > 20000000) {
      if (self) { // center team progress icon or self
        const lngLat = self.getLngLat()
        mapRef.flyTo({
          center: [lngLat.lng, lngLat.lat],
          zoom: 3,
          speed: 1.2
        })
      } else {
        mapRef.flyTo({
          center: map.waypoints[0].coordinates,
          zoom: 2.5,
          speed: 1.2
        })
      }
    }

    return () => {
      markers.forEach(marker => marker.remove())
    }
  }, [progressPoints])
}

const Map = ({ map, challengeMeasure, challengeGoal, progressPoints, user, points, distanceCallback }) => {
  const [currentLine, setCurrentLine] = useState(null)
  const [loaded, setLoaded] = useState(false)

  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  
  const getDirections = async (waypoints, directionType = 'driving') => {
    const pts = waypoints
      .filter(item => item.coordinates)
      .map(item => item.coordinates.toString()).join(';')
    let url = `https://api.mapbox.com/directions/v5/mapbox/${directionType}/${pts}.json?geometries=geojson&overview=full&alternatives=false&access_token=${process.env.MAPBOX}`
    const res = await fetch(url)
    if (!res.ok) {
      const err = await res.text()
      console.log(err)
    } else {
      return res.json()
    }
  }
  
  const buildLine = (waypts) => {
    let distance = 0
    const l = waypts
      .filter(item => item.coordinates)
      .reduce((memo, wp, i) => {
        if (i === 0) return memo
        const start = point(waypts[i - 1].coordinates)
        const end = point(wp.coordinates)
        const greatCircleLineString = greatCircle(start, end)
        memo.coords = [ ...memo.coords, ...greatCircleLineString.geometry.coordinates ]
        memo.lineStrings.push(greatCircleLineString)
        distance += length(greatCircleLineString, { units: 'meters' })
        return memo
      }, { coords: [], lineStrings: [] })
    if (!l || l.coords.length < 2) return []
    const line = lineString(l.coords) 
    if (distanceCallback) distanceCallback(distance)
    return {
      lineString: line,
      lineStrings: l.lineStrings,
      distance
    }
  }

  const addLine = () => {
    if (!mapRef.current) return
    mapRef.current.addLayer({
      id: 'line-layer',
      type: 'line',
      source: 'line',
      layout: {
          'line-join': 'round',
          'line-cap': 'round'
      },
      paint: {
          'line-color': '#FF9E00', // Line color
          'line-width': 4 // Line width
      }
    })
  }

  const updateLine = (line) => {
    if (!line) return
    if (!mapRef.current) return
    
    setCurrentLine(line) // save for progress points
    const existingLine = mapRef.current.getSource('line')
    if (existingLine) {
      existingLine.setData(line.lineString)
    } else {
      mapRef.current.addSource('line', {
        type: 'geojson',
        data: line.lineString
      })
      addLine()
    }
    setTimeout(() => {
      if (line.distance < 20000000) { // more than 1/2 earth circumference
        mapRef.current.fitBounds(bbox(line.lineString), { padding: 80 })
      }
    }, 400)
  }

  const mapTypes = {
    line: async () => {
      const line = buildLine(map.waypoints)
      updateLine(line)
    },
    file: async () => {
      const fileUrl = map.file.match(/^http/) ? map.file : process.env.CDN + map.file
      const res = await fetch(fileUrl)
      const r = await res.json()
      const line = lineString(r.route)
      const distance = length(line, { units: 'meters' })
      if (distanceCallback) distanceCallback(distance)
      const l = {
        distance,
        lineString: line,
        lineStrings: [line]
      }
      updateLine(l)
    },
    default: async () => {
      // no route provided, so get waypoints and request directions
      const withCoordinates = map.waypoints.filter(item => {
        if (item.coordinates) return true
        return false
      })
    
      if (!withCoordinates.length || withCoordinates.length < 2) return
      const dir = await getDirections(map.waypoints, map.directionType || 'driving')
        
      // no directions available, so revert to simple line between points
      if (!dir || !dir.routes || !dir.routes.length) {
        const line = buildLine(map.waypoints)
        updateLine(line)
        return
      }

      const line = lineString(dir.routes[0].geometry.coordinates)
      const distance = length(line, { units: 'meters' })
      if (distanceCallback) distanceCallback(distance)
      const l = {
        distance,
        lineString: line,
        lineStrings: [line]
      }
      updateLine(l)
    }
  }

  useEffect(() => {
    if (mapRef.current) return
    if (!mapContainerRef.current) return
    
    const mapConfig = {
      style: map.style ? `${map.style}` : 'mapbox://styles/mapbox/streets-v12?optimize=true',
      container: mapContainerRef.current,
      scrollZoom: false
    }
    
    // when in edit mode, no center provided
    if (map.waypoints && map.waypoints.length) {
      // Calculate the center of the waypoints
      const waypointGeojson = {
        type: 'FeatureCollection',
        features: map.waypoints.filter(item => (item.coordinates && item.coordinates.length)).map(item => {
          return {
            type: "Feature",
            geometry: { type: "Point", coordinates: item.coordinates.map(n => parseFloat(n)) }
          }
        })
      }
      mapConfig.center = map.waypoints[0].coordinates.map(n => parseFloat(n))
      mapConfig.zoom = 4
    } else {
      mapConfig.center = [-74.0127190981567, 40.70406422711367]
      mapConfig.zoom = 2
    }
    const m = new mapboxgl.Map(mapConfig)
    m.dragPan.disable(); // Disable dragging
    m.boxZoom.disable(); // Disable box zoom
    m.dragRotate.disable(); // Disable rotating
    m.doubleClickZoom.disable(); // Disable double-click zoom

    m.on('style.load', onMapLoad)
    m.on('click', () => {
      m.scrollZoom.enable()
      m.dragPan.enable()
      m.boxZoom.enable()
      m.dragRotate.enable()
      m.doubleClickZoom.enable()
    })
    
    mapRef.current = m
    
    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [mapRef])

  // was needed to update map when editing
  useMemo(() => {
    if (!mapRef.current?.isStyleLoaded()) return
    if (map.waypoints.length > 1) {
      // construct the map route or line
      const type = (map.line || map.type === 'line') ? 'line' : (map.file ? 'file' : 'default')
      if (mapTypes[type]) { mapTypes[type]() } else { mapTypes.default() }
    } else {
      mapRef.current?.removeLayer('line-layer')
    }
  }, [map.waypoints, map.type, map.directionType])

  const onMapLoad = (a) => {
    if (!a) return
    if (map.waypoints.length > 1) {
      // construct the map route or line
      const type = (map.line || map.type === 'line') ? 'line' : (map.file ? 'file' : 'default')
      if (mapTypes[type]) { mapTypes[type]() } else { mapTypes.default() }
    }
    setLoaded(true) // will trigger rendernig of waypoints and participants
  }

  // used for updates to map, mostly in edit mode
  useMemo(() => {
    if (!mapRef.current) return
    onMapLoad()
  }, [map])

  return <>
    <div style={{ width: '100%', height: '100%' }} id='map-container' ref={mapContainerRef}/>
    {loaded && <>
      <WaypointMarkers mapRef={mapRef.current} waypoints={map.waypoints} defaultIconColor={map.defaultIconColor} showStreetView={map.showStreetView} />
      {(Boolean(map?.waypoints?.length) && currentLine) && 
        <ParticipantMarkers 
          mapRef={mapRef.current} 
          map={map} 
          user={user}
          points={points}
          progressPoints={progressPoints} 
          currentLine={currentLine} 
          challengeMeasure={challengeMeasure} 
          challengeGoal={challengeGoal}
          />}
    </>}
  </>
  
}

export default MapContainer
